<template>
    <div class="modal fade" id="readerColorPickerModal" tabindex="-1" aria-labelledby="readerColorPickerModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <h5 class="modal-title" id="readerColorPickerModalLabel">{{ title }}</h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" :aria-label="$t('colorPicker.close')"></button>
                </div>
                <div class="modal-body">
                    <label class="form-label">{{ $t('colorPicker.predefined') }}</label>
                    <div class="preset-colors mb-4">
                        <button
                            v-for="color in presetColors"
                            :key="color"
                            type="button"
                            class="preset-color"
                            :class="{ selected: selectedColor === color }"
                            :style="{ backgroundColor: color }"
                            :title="color"
                            :aria-label="$t('colorPicker.selectColor', { color })"
                            @click="selectPreset(color)"
                        >
                            <i v-if="selectedColor === color" class="mdi mdi-check" :class="checkColor(color)"></i>
                        </button>
                    </div>

                    <template v-if="allowCustomColor">
                    <label for="custom-reader-color" class="form-label">{{ $t('colorPicker.custom') }}</label>
                    <div class="custom-color-picker">
                        <div
                            id="custom-reader-color"
                            class="color-preview"
                            :style="{ backgroundColor: selectedColor }"
                            aria-hidden="true"
                        ></div>
                        <canvas
                            ref="colorWheelCanvas"
                            class="color-wheel"
                            width="220"
                            height="220"
                            role="img"
                            :aria-label="$t('colorPicker.wheel')"
                            @pointerdown="startColorWheelDrag"
                        ></canvas>
                        <div class="input-group color-hex-input">
                            <span class="input-group-text">#</span>
                            <input
                                type="text"
                                class="form-control text-uppercase"
                                :class="{ 'is-invalid': hexError }"
                                v-model="hexInput"
                                maxlength="6"
                                :aria-label="$t('colorPicker.hex')"
                                @input="updateFromHex"
                            />
                            <div v-if="hexError" class="invalid-feedback">{{ $t('colorPicker.hexError') }}</div>
                        </div>
                    </div>
                    </template>
                </div>
                <div class="modal-footer justify-content-center">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('colorPicker.cancel') }}</button>
                    <button type="button" class="btn btn-primary" @click="confirmColor">{{ $t('colorPicker.select') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
    name: 'ColorPickerModal',
    emits: ['colorSelected'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const presetColors = ['#000000', '#FF0000', '#00FF00', '#FFA500', '#0000FF', '#FF00FF', '#00FFFF', '#FFFFFF'];
        const title = ref(t('colorPicker.title'));
        const fieldKey = ref('');
        const selectedColor = ref('#000000');
        const hexInput = ref('000000');
        const hexError = ref(false);
        const colorWheelCanvas = ref(null);
        const allowCustomColor = ref(true);

        const open = (key, label, color, allowCustom = true) => {
            fieldKey.value = key;
            title.value = label;
            allowCustomColor.value = allowCustom;
            setSelectedColor(color);
            hexError.value = false;
            if(allowCustomColor.value) nextTick(drawColorWheel);
        };

        const selectPreset = (color) => {
            setSelectedColor(color);
            hexError.value = false;
            nextTick(drawColorWheel);
        };

        const syncHexInput = () => {
            hexInput.value = selectedColor.value.slice(1).toUpperCase();
            hexError.value = false;
            nextTick(drawColorWheel);
        };

        const updateFromHex = () => {
            const value = hexInput.value.replace(/^#/, '').toUpperCase();
            hexInput.value = value;
            hexError.value = !/^[0-9A-F]{6}$/.test(value);
            if(!hexError.value) {
                setSelectedColor(`#${value}`);
                nextTick(drawColorWheel);
            }
        };

        const setSelectedColor = (color) => {
            selectedColor.value = color.toUpperCase();
            hexInput.value = selectedColor.value.slice(1);
        };

        const drawColorWheel = () => {
            const canvas = colorWheelCanvas.value;
            if(!canvas) return;

            const context = canvas.getContext('2d');
            const size = canvas.width;
            const radius = size / 2;
            const image = context.createImageData(size, size);

            for(let y = 0; y < size; y++) {
                for(let x = 0; x < size; x++) {
                    const dx = x - radius;
                    const dy = y - radius;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const index = (y * size + x) * 4;

                    if(distance <= radius) {
                        const hue = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
                        const saturation = distance / radius;
                        const { red, green, blue } = hsvToRgb(hue, saturation, 1);
                        image.data[index] = red;
                        image.data[index + 1] = green;
                        image.data[index + 2] = blue;
                        image.data[index + 3] = 255;
                    }
                }
            }

            context.putImageData(image, 0, 0);
            drawColorWheelMarker(context, radius);
        };

        const drawColorWheelMarker = (context, radius) => {
            const rgb = hexToRgb(selectedColor.value);
            const hsv = rgbToHsv(rgb.red, rgb.green, rgb.blue);
            const markerRadius = hsv.saturation * radius;
            const angle = hsv.hue * Math.PI / 180;
            const x = radius + Math.cos(angle) * markerRadius;
            const y = radius + Math.sin(angle) * markerRadius;

            context.beginPath();
            context.arc(x, y, 6, 0, Math.PI * 2);
            context.lineWidth = 3;
            context.strokeStyle = '#ffffff';
            context.stroke();
            context.lineWidth = 1;
            context.strokeStyle = '#313a46';
            context.stroke();
        };

        const startColorWheelDrag = (event) => {
            event.preventDefault();
            selectColorFromWheel(event);
            window.addEventListener('pointermove', selectColorFromWheel);
            window.addEventListener('pointerup', stopColorWheelDrag, { once: true });
        };

        const stopColorWheelDrag = () => {
            window.removeEventListener('pointermove', selectColorFromWheel);
        };

        const selectColorFromWheel = (event) => {
            const canvas = colorWheelCanvas.value;
            if(!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const scale = canvas.width / rect.width;
            const radius = canvas.width / 2;
            const x = (event.clientX - rect.left) * scale;
            const y = (event.clientY - rect.top) * scale;
            const dx = x - radius;
            const dy = y - radius;
            const distance = Math.min(radius, Math.sqrt(dx * dx + dy * dy));
            const hue = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
            const saturation = distance / radius;
            const rgb = hsvToRgb(hue, saturation, 1);

            setSelectedColor(`#${toHex(rgb.red)}${toHex(rgb.green)}${toHex(rgb.blue)}`);
            hexError.value = false;
            drawColorWheel();
        };

        const hexToRgb = (color) => {
            const value = color.replace(/^#/, '');
            return {
                red: parseInt(value.slice(0, 2), 16),
                green: parseInt(value.slice(2, 4), 16),
                blue: parseInt(value.slice(4, 6), 16),
            };
        };

        const hsvToRgb = (hue, saturation, value) => {
            const chroma = value * saturation;
            const x = chroma * (1 - Math.abs((hue / 60) % 2 - 1));
            const match = value - chroma;
            let red = 0;
            let green = 0;
            let blue = 0;

            if(hue < 60) [red, green, blue] = [chroma, x, 0];
            else if(hue < 120) [red, green, blue] = [x, chroma, 0];
            else if(hue < 180) [red, green, blue] = [0, chroma, x];
            else if(hue < 240) [red, green, blue] = [0, x, chroma];
            else if(hue < 300) [red, green, blue] = [x, 0, chroma];
            else [red, green, blue] = [chroma, 0, x];

            return {
                red: Math.round((red + match) * 255),
                green: Math.round((green + match) * 255),
                blue: Math.round((blue + match) * 255),
            };
        };

        const rgbToHsv = (red, green, blue) => {
            const r = red / 255;
            const g = green / 255;
            const b = blue / 255;
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const delta = max - min;
            let hue = 0;

            if(delta !== 0) {
                if(max === r) hue = 60 * (((g - b) / delta) % 6);
                else if(max === g) hue = 60 * ((b - r) / delta + 2);
                else hue = 60 * ((r - g) / delta + 4);
            }

            return {
                hue: (hue + 360) % 360,
                saturation: max === 0 ? 0 : delta / max,
                value: max,
            };
        };

        const toHex = (value) => value.toString(16).padStart(2, '0').toUpperCase();

        const confirmColor = () => {
            updateFromHex();
            if(hexError.value) return;
            emit('colorSelected', fieldKey.value, parseInt(hexInput.value, 16));
            document.querySelector('#readerColorPickerModal [data-bs-dismiss="modal"]').click();
        };

        const checkColor = (color) => ['#FFFFFF', '#FFA500', '#00FFFF', '#00FF00'].includes(color) ? 'text-dark' : 'text-white';

        onBeforeUnmount(stopColorWheelDrag);

        return { presetColors, title, selectedColor, hexInput, hexError, colorWheelCanvas, allowCustomColor, open, selectPreset, syncHexInput, updateFromHex, startColorWheelDrag, confirmColor, checkColor };
    },
};
</script>

<style scoped>
.preset-colors {
    display: grid;
    grid-template-columns: repeat(8, 40px);
    gap: 12px;
}

.preset-color {
    width: 40px;
    height: 40px;
    padding: 0;
    border: 1px solid #adb5bd;
    border-radius: 50%;
    font-size: 1.25rem;
}

.preset-color.selected {
    border: 3px solid var(--ct-primary-color);
    box-shadow: 0 0 0 2px rgba(80, 108, 240, 0.2);
}

.custom-color-picker {
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns: 72px 1fr;
}

.color-preview {
    border: 1px solid #adb5bd;
    border-radius: 6px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
    height: 72px;
    width: 72px;
}

.color-wheel {
    aspect-ratio: 1;
    cursor: crosshair;
    justify-self: center;
    max-width: 220px;
    touch-action: none;
    width: 100%;
}

.color-hex-input {
    grid-column: 1 / -1;
}

@media (max-width: 575.98px) {
    .preset-colors {
        grid-template-columns: repeat(4, 40px);
    }

    .custom-color-picker {
        grid-template-columns: 1fr;
    }

    .color-preview {
        width: 100%;
    }

    .color-wheel {
        max-width: 240px;
    }
}
</style>
